import {
  nanoSecToDate,
  nanoSecToLocalTimestamp,
  nanoSecToSecStr,
} from "@/shared/time";

/**
 * How a wall-clock epoch-nanosecond timestamp should be rendered to text.
 *
 * Enum values are the stable identifiers used for persistence
 * (localStorage); display labels live in {@link TIMESTAMP_FORMAT_LABELS}.
 * Pure formatting — no React, no workspace access.
 *
 * Note: relative-to-visualizer and relative-to-context formats existed
 * previously but required a live reference to the visualizer playhead,
 * which the chat does not currently have access to (ChatSidebar is
 * rendered in the collaboration bar, outside the Workspace provider
 * subtree). Both were removed rather than shipped broken; they can come
 * back once a shared timer/bounds context is lifted to app-layout level.
 */
export enum TimestampFormat {
  Nanoseconds = "ns",
  Microseconds = "us",
  Milliseconds = "ms",
  SecondsNanos = "seconds-nanos",
  Utc = "utc",
  LocalTime = "local",
}

interface TimestampFormatSpec {
  readonly label: string;
  readonly description: string;
  readonly format: (ns: bigint) => string;
}

/**
 * Single source of truth for every per-format fact. Adding a new
 * `TimestampFormat` enum member without extending this table is a TS error
 * (`Record<TimestampFormat, ...>` exhaustiveness). Declaration order also
 * fixes the order {@link TIMESTAMP_FORMATS} exposes — i.e. the order the
 * radio group iterates.
 */
const FORMAT_SPECS: Record<TimestampFormat, TimestampFormatSpec> = {
  [TimestampFormat.Nanoseconds]: {
    label: "Nanoseconds (ns)",
    description: "Raw epoch nanoseconds (19-digit integer).",
    format: (ns) => `${ns.toString()} ns`,
  },
  [TimestampFormat.Microseconds]: {
    label: "Microseconds (μs)",
    description: "Epoch microseconds; sub-μs remainder dropped.",
    format: (ns) => `${(ns / 1_000n).toString()} μs`,
  },
  [TimestampFormat.Milliseconds]: {
    label: "Milliseconds (ms)",
    description: "Epoch milliseconds; sub-ms remainder dropped.",
    format: (ns) => `${(ns / 1_000_000n).toString()} ms`,
  },
  [TimestampFormat.SecondsNanos]: {
    label: "Seconds.nanos",
    description: "Fixed-point seconds.nanoseconds (e.g. 1771481251.468476035).",
    format: (ns) => `${nanoSecToSecStr(ns)} s`,
  },
  [TimestampFormat.Utc]: {
    label: "UTC (ISO 8601)",
    description: "ISO 8601 UTC, millisecond precision.",
    format: (ns) => nanoSecToDate(ns).toISOString(),
  },
  [TimestampFormat.LocalTime]: {
    label: "Local time",
    description: "Browser-local date & time.",
    format: (ns) => nanoSecToLocalTimestamp(ns),
  },
};

// --- Derived exports below this line. Edit FORMAT_SPECS, not these. ---

export const DEFAULT_TIMESTAMP_FORMAT: TimestampFormat =
  TimestampFormat.SecondsNanos;

export const TIMESTAMP_FORMATS: readonly TimestampFormat[] = Object.keys(
  FORMAT_SPECS,
) as TimestampFormat[];

function projectSpecs<K extends keyof TimestampFormatSpec>(
  key: K,
): Record<TimestampFormat, TimestampFormatSpec[K]> {
  return Object.fromEntries(
    (
      Object.entries(FORMAT_SPECS) as [TimestampFormat, TimestampFormatSpec][]
    ).map(([k, v]) => [k, v[key]]),
  ) as Record<TimestampFormat, TimestampFormatSpec[K]>;
}

export const TIMESTAMP_FORMAT_LABELS = projectSpecs("label");
export const TIMESTAMP_FORMAT_DESCRIPTIONS = projectSpecs("description");

/**
 * Render a wall-clock ns bigint in the requested format. Every numeric
 * format carries its own unit suffix (`ns`, `μs`, `ms`, `s`). ISO 8601 and
 * local-time strings are self-describing and carry no suffix.
 */
export function formatTimestamp(ns: bigint, format: TimestampFormat): string {
  return FORMAT_SPECS[format].format(ns);
}

export function isTimestampFormat(value: unknown): value is TimestampFormat {
  return (
    typeof value === "string" &&
    (TIMESTAMP_FORMATS as readonly string[]).includes(value)
  );
}
